/* =====================================================================================
   The criteria editor on its own, without a results table attached.

   WHY IT WAS SPLIT OUT. The builder used to own the criteria AND render the answer, which made
   search a place you went rather than something you did to what was already on screen. The Atlas
   ended up with two filtering systems that could not see each other: the country and segment
   pickers drove the map and the table, the criteria drove a separate tab, and narrowing one did
   nothing to the other. Asking for the hundred best mid-size units left the map showing all
   1,433.

   So the editor is a component and the criteria are the HOST'S state. The Atlas keeps one set and
   feeds it to the map, the table and the export alike. SearchBuilder still exists, still owns its
   own state, and still serves Circus: this is the same editor with the results detached.

   THE MUST AND PREFER TOGGLE IS THE POINT OF THE SCREEN, so it is a visible control on every row
   rather than a setting somewhere. Getting it wrong is what makes a search useless in both
   directions: everything as MUST returns four rows, everything as PREFER returns the whole list
   in a slightly different order.
   ===================================================================================== */

import { useMemo } from 'react';
import { isComplete, opsFor, type Criterion, type Op, type SearchField } from '../lib/search';
import { Combo } from './Combo';
import './searchbuilder.css';

export interface Preset<T> {
  key: string; name: string; note: string;
  build: (f: SearchField<T>[]) => Criterion[];
}

export function CriteriaPanel<T extends Record<string, unknown>>({
  fields, criteria, setCriteria, presets, text, setText, extra,
}: {
  fields: SearchField<T>[];
  criteria: Criterion[];
  setCriteria: (next: Criterion[]) => void;
  presets?: Preset<T>[];
  text?: string;
  setText?: (s: string) => void;
  /** Buttons the host wants beside the controls: save, export, act on the results. */
  extra?: React.ReactNode;
}) {
  const byKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields]);

  function addCriterion(fieldKey?: string) {
    const f = fieldKey ? byKey.get(fieldKey) : fields[0];
    if (!f) return;
    setCriteria([...criteria, {
      field: f.key, op: opsFor(f.type)[0].op,
      value: f.type === 'number' && f.suggest ? f.suggest : undefined,
      /* A number defaults to PREFER and everything else to MUST, because that is what people
         mean: "in the United States" is a requirement, "about this big" is a preference. */
      mode: f.type === 'number' ? 'prefer' : 'must',
      weight: 1,
    }]);
  }

  const update = (i: number, patch: Partial<Criterion>) =>
    setCriteria(criteria.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const remove = (i: number) => setCriteria(criteria.filter((_, j) => j !== i));

  return (
    <div className="sb">
      <div className="sb-bar">
        {setText && (
          <input className="input grow" placeholder="Search by name, city or assessment"
                 value={text ?? ''} onChange={(e) => setText(e.target.value)} />
        )}
        <button className="btn" onClick={() => addCriterion()}>Add a criterion</button>
        {extra}
      </div>

      {presets && presets.length > 0 && (
        <div className="sb-presets">
          {presets.map((p) => (
            <button key={p.key} className="sb-preset" title={p.note}
                    onClick={() => setCriteria([...criteria, ...p.build(fields)])}>
              {p.name}
            </button>
          ))}
          {criteria.length > 0 && (
            <button className="sb-preset clear" onClick={() => setCriteria([])}>Clear all</button>
          )}
        </div>
      )}

      {criteria.map((c, i) => {
        const f = byKey.get(c.field);
        if (!f) return null;
        const ops = opsFor(f.type);
        const needsValue = !['present', 'absent'].includes(c.op);
        /* An unfinished criterion does nothing, and silently doing nothing is how a filter gets
           trusted that was never applied. Marked rather than auto-removed, because the row is
           usually half-typed rather than abandoned. */
        const done = isComplete(c);
        return (
          <div className={`sb-row${done ? '' : ' incomplete'}`} key={i}>
            {/* MUST or PREFER, first, because it changes what every other control means. */}
            <button className={`sb-mode ${c.mode}`}
                    title={c.mode === 'must'
                      ? 'Excludes anything that fails this'
                      : 'Ranks higher, but nothing is excluded'}
                    onClick={() => update(i, { mode: c.mode === 'must' ? 'prefer' : 'must' })}>
              {c.mode === 'must' ? 'must' : 'prefer'}
            </button>

            <select className="input sel" value={c.field}
                    onChange={(e) => {
                      const nf = byKey.get(e.target.value)!;
                      update(i, { field: nf.key, op: opsFor(nf.type)[0].op,
                                  value: nf.type === 'number' && nf.suggest ? nf.suggest : undefined });
                    }}>
              {fields.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
            </select>

            <select className="input sel narrow" value={c.op}
                    onChange={(e) => update(i, { op: e.target.value as Op })}>
              {ops.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
            </select>

            {needsValue && f.type === 'number' && c.op === 'between' && (
              <span className="sb-range">
                <input className="input" type="number" value={(c.value as number[])?.[0] ?? ''}
                       onChange={(e) => update(i, { value: [Number(e.target.value),
                                                            (c.value as number[])?.[1] ?? 0] })} />
                <span>to</span>
                <input className="input" type="number" value={(c.value as number[])?.[1] ?? ''}
                       onChange={(e) => update(i, { value: [(c.value as number[])?.[0] ?? 0,
                                                            Number(e.target.value)] })} />
              </span>
            )}

            {needsValue && f.type === 'number' && c.op !== 'between' && (
              <input className="input" type="number" value={(c.value as number) ?? ''}
                     onChange={(e) => update(i, { value: Number(e.target.value) })} />
            )}

            {/* Typed, not scrolled, once the list is long enough that scrolling is hunting. The
                same treatment the Atlas toolbar already had; this control was missed, so the
                country list here stayed a native select of 78 entries. A native select's own
                keyboard matching only works from the first letter and resets between keystrokes,
                which is no help at all for "United Arab Emirates". */}
            {needsValue && (f.type === 'enum' || f.type === 'boolean' || f.type === 'array')
              && f.options && f.options.length > 12 && (
              <Combo
                value={(c.value as string) ?? ''}
                onChange={(v) => update(i, { value: v })}
                options={f.options}
                ariaLabel={f.label}
                placeholder="Type to find"
                width={230}
              />
            )}

            {needsValue && (f.type === 'enum' || f.type === 'boolean' || f.type === 'array')
              && f.options && f.options.length <= 12 && (
              <select className="input sel" value={(c.value as string) ?? ''}
                      onChange={(e) => update(i, { value: e.target.value })}>
                <option value="">choose</option>
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}

            {needsValue && (f.type === 'text' || (!f.options && f.type === 'array')) && (
              <input className="input grow" value={(c.value as string) ?? ''}
                     placeholder="value"
                     onChange={(e) => update(i, { value: e.target.value })} />
            )}

            {c.mode === 'prefer' && (
              <label className="sb-weight" title="How much this preference pulls the ranking">
                x
                <input className="input" type="number" min={0.5} max={5} step={0.5}
                       value={c.weight ?? 1}
                       onChange={(e) => update(i, { weight: Number(e.target.value) })} />
              </label>
            )}

            <button className="sb-x" onClick={() => remove(i)} aria-label="Remove">✕</button>
            {!done && <p className="sb-hint sb-incomplete">Not applied: this needs a value.</p>}
            {done && f.hint && <p className="sb-hint">{f.hint}</p>}
          </div>
        );
      })}
    </div>
  );
}

/* A search that returns nothing is a dead end unless it says which requirement did it. Shared so
   the Atlas and Circus give the same explanation rather than each inventing one. */
export function ResultCount<T>({ shown, total, criteria, excludedBy, fields }: {
  shown: number; total: number; criteria: Criterion[];
  excludedBy: Record<string, number>; fields: SearchField<T>[];
}) {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const worst = Object.entries(excludedBy).sort((a, b) => b[1] - a[1])[0];
  if (total === 0 && criteria.length > 0) {
    return (
      <div className="sb-count">
        Nothing matched.
        {worst && (
          <> The narrowest requirement was{' '}
            <strong>{byKey.get(worst[0])?.label}, which excluded{' '}
              {worst[1].toLocaleString('en-GB')}</strong>. Try switching it from must to prefer.
          </>
        )}
      </div>
    );
  }
  return (
    <div className="sb-count">
      Showing <strong>{shown.toLocaleString('en-GB')}</strong> of{' '}
      <strong>{total.toLocaleString('en-GB')}</strong> that match
      {criteria.some((c) => c.mode === 'prefer') && ', best fit first'}.
    </div>
  );
}
